import SuggestProperty from './pages/SuggestProperty';
import CommunityPage from './pages/CommunityPage';
import MyCommunityPage from './pages/MyCommunityPage';
import { PropertyProvider } from './contexts/PropertyContext';
import React, { useEffect, lazy, Suspense } from "react";
import CanonicalTag from "./components/CanonicalTag";
import DailyIncomeBar from "./components/DailyIncomeBar";
import "./App.css";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
const PageSkeleton = lazy(() => import('./components/PageSkeleton'));
import { AuthProvider, useAuth } from "./contexts/AuthContext";
const Home = lazy(() => import('./pages/Home'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Benefits = lazy(() => import('./pages/Benefits'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const PropertyTradingInterface = lazy(() => import('./pages/PropertyTradingInterface'));

const Properties = lazy(() => import("./pages/Properties"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const InvestorDashboard = lazy(() => import("./pages/InvestorDashboard"));
const Holdings = lazy(() => import("./pages/Holdings"));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const AdminLogs = lazy(() => import("./pages/AdminLogs"));
const AddFunds = lazy(() => import('./pages/AddFunds'));
const PaymentSettings = lazy(() => import('./pages/PaymentSettings'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const LegalDocPage = lazy(() => import('./pages/LegalDocPage'));
const AdminRoute = lazy(() => import('./components/AdminRoute'));
const AdminOps = lazy(() => import("./pages/AdminOps"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ForgotPasswordEmail = lazy(() => import('./pages/ForgotPasswordEmail'));
const VerifyResetCode = lazy(() => import('./pages/VerifyResetCode'));
const SetNewPassword = lazy(() => import('./pages/SetNewPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const TradeHistory = lazy(() => import('./pages/TradeHistory'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const AdminDeposits = lazy(() => import('./pages/AdminDeposits'));
const WithdrawFunds = lazy(() => import('./pages/WithdrawFunds'));
const MyBankAccount = lazy(() => import('./pages/MyBankAccount'));
const RentalIncome = lazy(() => import('./pages/RentalIncome'));
const AdminWithdrawals = lazy(() => import('./pages/AdminWithdrawals'));
const AdminNumbersConfig = lazy(() => import('./pages/AdminNumbersConfig'));
const TradingMarketplace = lazy(() => import('./pages/TradingMarketplace'));
const Payouts = lazy(() => import('./pages/Payouts'));
const TwoFactorSetup = lazy(() => import('./pages/TwoFactorSetup'));
const AdminKYC = lazy(() => import('./pages/AdminKYC'));
const AdminProperties = lazy(() => import('./pages/AdminProperties'));
const AdminOrders = lazy(() => import('./pages/AdminOrders'));
const AdminPaymentSettings = lazy(() => import('./pages/AdminPaymentSettings'));
const AdminRentalIncome = lazy(() => import('./pages/AdminRentalIncome'));
const AdminNAVManagement = lazy(() => import('./pages/AdminNAVManagement'));
const AdminTransactionHistory = lazy(() => import('./pages/AdminTransactionHistory'));
const AdminNotifications = lazy(() => import('./pages/AdminNotifications'));
const AdminNotificationsInbox = lazy(() => import('./pages/AdminNotificationsInbox'));
const AdminFinancialSafeguards = lazy(() => import('./pages/AdminFinancialSafeguards'));
const AdminFintechControls = lazy(() => import('./pages/AdminFintechControls'));
const AdminTokenManagement = lazy(() => import('./pages/AdminTokenManagement'));
const AdminDatabaseBackup = lazy(() => import('./pages/AdminDatabaseBackup'));
const AdminBackups = lazy(() => import('./pages/AdminBackups'));
const AdminCompliance = lazy(() => import('./pages/AdminCompliance'));
const AdminTreasury = lazy(() => import('./pages/AdminTreasury'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const AdminBankReconciliation = lazy(() => import('./pages/AdminBankReconciliation'));
const AdminBankAccountVerifications = lazy(() => import('./pages/AdminBankAccountVerifications'));
const AdminPayoutSop = lazy(() => import('./pages/AdminPayoutSop'));
const AdminDailyOps = lazy(() => import('./pages/AdminDailyOps'));
const AdminMonitoring = lazy(() => import('./pages/AdminMonitoring'));
import AdminSupport from "./admin/AdminSupport";
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/AdminUserDetail'));
const AdminLeads = lazy(() => import('./pages/AdminLeads'));
const ReferralDashboard = lazy(() => import('./pages/ReferralDashboard'));
const ReferralInvitePage = lazy(() => import('./pages/ReferralInvitePage'));
const TransactionsHub = lazy(() => import('./pages/TransactionsHub'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const InvestorInfo = lazy(() => import('./pages/InvestorInfo'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const ManualPaymentAdmin = lazy(() => import('./pages/ManualPaymentAdmin'));
const KYCUpload = lazy(() => import('./pages/KYCUpload'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const LegalStructure = lazy(() => import('./pages/LegalStructure'));
const LiquidityMechanism = lazy(() => import('./pages/LiquidityMechanism'));
const InvestorFAQ = lazy(() => import('./pages/InvestorFAQ'));
const MetaLanding = lazy(() => import('./pages/MetaLanding'));
const NavMethodology = lazy(() => import('./pages/NavMethodology'));
const RiskDisclosure = lazy(() => import('./pages/RiskDisclosure'));
const InvestmentStrategy = lazy(() => import('./pages/InvestmentStrategy'));
const ConflictOfInterest = lazy(() => import('./pages/ConflictOfInterest'));
const GovernanceFramework = lazy(() => import('./pages/GovernanceFramework'));
const SystemStatus = lazy(() => import('./pages/SystemStatus'));
const FeeSchedule = lazy(() => import('./pages/FeeSchedule'));
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Toaster as HotToaster } from "react-hot-toast";
import { initVersionCheck, APP_VERSION } from "./utils/versionManager";
const ErrorBoundary = lazy(() => import('./components/ErrorBoundary'));
const PublicLayout = lazy(() => import('./components/layout/PublicLayout'));
const SystemBanner = lazy(() => import('./components/SystemBanner'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const FloatingReferral = lazy(() => import('./components/FloatingReferral'));
import { apiRequest } from "./api/apiClient";

// Initialize Firebase
import "./utils/firebase";
const SalariedInvestorPage = lazy(() => import('./pages/SalariedInvestorPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));

const ProtectedRoute = ({ children }) => {
  const { user, loading, loadingAuth, forcePasswordReset } = useAuth();
  const location = useLocation();

  if (loading || loadingAuth) {
    return <PageSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If password reset is required and user is not on reset-password page, redirect
  if (forcePasswordReset && location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace />;
  }

  // Phone number is required for all accounts — redirect until provided
  if (!user.phone && location.pathname !== "/complete-profile") {
    return <Navigate to="/complete-profile" replace />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Global phone-number enforcement — runs on every route, public or protected.
  // Closes the gap where a logged-in user without a phone could land on a
  // public page (e.g. /properties) that bypasses ProtectedRoute entirely.
  useEffect(() => {
    const exemptPaths = ["/complete-profile", "/login", "/register", "/signup", "/logout"];
    if (user && !user.phone && !exemptPaths.includes(location.pathname)) {
      navigate("/complete-profile", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // DISABLED: Backend URL cache check - now using dynamic URL detection
  // The cache check was causing infinite reload loops
  useEffect(() => {
    console.log("🌍 Checking Authentication State…");
    // Clean up old backend URL cache to prevent future issues
    const oldCache = localStorage.getItem("_backend_url_cache");
    if (oldCache) {
      console.log("🧹 Cleaning up old backend URL cache");
      localStorage.removeItem("_backend_url_cache");
    }
  }, []);

  // Initialize version checking on mount
  useEffect(() => {
    console.log(`📱 EstateCoin App Version: ${APP_VERSION}`);
    initVersionCheck();
  }, []);

  return (
    <>
      <CanonicalTag />
      <DailyIncomeBar />
      <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/start" element={<Navigate to="/" replace />} />
        <Route path="/benefits" element={<Benefits />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/register" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/forgot-password-email"
          element={<ForgotPasswordEmail />}
        />
        <Route path="/verify-reset-code" element={<VerifyResetCode />} />
        <Route path="/set-new-password" element={<SetNewPassword />} />
        <Route
          path="/reset-password"
          element={
            <ProtectedRoute>
              <ResetPassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/complete-profile"
          element={
            <ProtectedRoute>
              <CompleteProfile />
            </ProtectedRoute>
          }
        />
        <Route path="/verify-certificate/:certificateNumber?" element={<VerifyCertificate />} />
        <Route path="/payouts" element={<Payouts />} />
        <Route path="/terms-and-conditions" element={<TermsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/legal/:docSlug" element={<LegalDocPage />} />
        <Route path="/legal-structure" element={<LegalStructure />} />
        <Route path="/liquidity" element={<LiquidityMechanism />} />
        <Route path="/faq" element={<InvestorFAQ />} />
        <Route path="/invest/salaried-investor-india" element={<SalariedInvestorPage />} />
        <Route path="/invest/real-estate-vs-fd-vs-mutual-funds" element={<ComparisonPage />} />
        <Route path="/invest" element={<MetaLanding />} />
        <Route path="/nav-methodology" element={<NavMethodology />} />
        <Route path="/risk-disclosure" element={<RiskDisclosure />} />
        <Route path="/investment-strategy" element={<InvestmentStrategy />} />
        <Route path="/conflict-of-interest" element={<ConflictOfInterest />} />
        <Route path="/governance-framework" element={<GovernanceFramework />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/system-status" element={<SystemStatus />} />
        <Route path="/fees" element={<FeeSchedule />} />
        <Route path="/fee-schedule" element={<FeeSchedule />} />
        <Route path="/transactions" element={<TransactionsHub />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/investor-info" element={<InvestorInfo />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/trading" element={<TradingMarketplace />} />
        <Route path="/community" element={<CommunityPage />} />
          <Route path="/my-community" element={<MyCommunityPage />} />
          <Route path="/suggest-property" element={<SuggestProperty />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/property-detail/:propertyId" element={<PropertyDetail />} />
        <Route path="/property/:id" element={<PropertyDetail />} />
        <Route
          path="/property/:id/trade"
          element={<PropertyTradingInterface />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/investor-dashboard"
          element={
            <ProtectedRoute>
              <InvestorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/investor"
          element={
            <ProtectedRoute>
              <InvestorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/holdings"
          element={
            <ProtectedRoute>
              <Holdings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-funds"
          element={
            <ProtectedRoute>
              <AddFunds />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <TradeHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/withdraw-funds"
          element={
            <ProtectedRoute>
              <WithdrawFunds />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-settings"
          element={
            <ProtectedRoute>
              <PaymentSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-bank-account"
          element={
            <ProtectedRoute>
              <MyBankAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification-settings"
          element={
            <ProtectedRoute>
              <NotificationSettings />
            </ProtectedRoute>
          }
        />
        <Route path="/referral" element={<ReferralInvitePage />} />
        <Route path="/invite" element={<ReferralInvitePage />} />
        <Route
          path="/referrals"
          element={
            <ProtectedRoute>
              <ReferralDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile-settings"
          element={
            <ProtectedRoute>
              <ProfileSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/security"
          element={
            <ProtectedRoute>
              <TwoFactorSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rental-income"
          element={
            <ProtectedRoute>
              <RentalIncome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kyc"
          element={
            <ProtectedRoute>
              <KYCUpload />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Admin Routes - Protected with AdminRoute */}
        <Route
          path="/admin"
            element={<AdminRoute><AdminDashboard /></AdminRoute>}
          />
          <Route
          path="/admin/ops"
          element={
            <AdminRoute>
              <AdminOps />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <AdminRoute>
              <AdminLogs />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/deposits"
          element={
            <AdminRoute>
              <AdminDeposits />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/withdrawals"
          element={
            <AdminRoute>
              <AdminWithdrawals />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/payout-sop"
          element={
            <AdminRoute>
              <AdminPayoutSop />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/daily-ops"
          element={
            <AdminRoute>
              <AdminDailyOps />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/numbers-config"
          element={
            <AdminRoute>
              <AdminNumbersConfig />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/kyc"
          element={
            <AdminRoute>
              <AdminKYC />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/properties"
          element={
            <AdminRoute>
              <AdminProperties />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/nav-management"
          element={
            <AdminRoute>
              <AdminNAVManagement />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <AdminRoute>
              <AdminOrders />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/payment-settings"
          element={
            <AdminRoute>
              <AdminPaymentSettings />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/rental-income"
          element={
            <AdminRoute>
              <AdminRentalIncome />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/transaction-history"
          element={
            <AdminRoute>
              <AdminTransactionHistory />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <AdminRoute>
              <AdminNotifications />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/notifications-inbox"
          element={
            <AdminRoute>
              <AdminNotificationsInbox />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/safeguards"
          element={
            <AdminRoute>
              <AdminFinancialSafeguards />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/fintech-controls"
          element={
            <AdminRoute>
              <AdminFintechControls />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/token-management"
          element={
            <AdminRoute>
              <AdminTokenManagement />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/database-backup"
          element={
            <AdminRoute>
              <AdminDatabaseBackup />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/backups"
          element={
            <AdminRoute>
              <AdminBackups />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/compliance"
          element={
            <AdminRoute>
              <AdminCompliance />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/treasury"
          element={
            <AdminRoute>
              <AdminTreasury />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminRoute>
              <AdminReports />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bank-reconciliation"
          element={
            <AdminRoute>
              <AdminBankReconciliation />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bank-account-verifications"
          element={
            <AdminRoute>
              <AdminBankAccountVerifications />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/monitoring"
          element={
            <AdminRoute>
              <AdminMonitoring />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <AdminRoute>
              <AdminLeads />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <AdminRoute>
              <AdminSupport />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <AdminRoute>
              <AdminUserDetail />
            </AdminRoute>
          }
        />

        {/* Manual Payment Admin - Separate from main admin system */}
        <Route path="/manual-admin" element={<ManualPaymentAdmin />} />
        {/* Catch-all: redirect unknown paths to home (SPA fallback) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      <FloatingReferral user={user} />
      <Toaster />
      <HotToaster position="top-right" />
    </>
  );
}

function App() {
  const [maintenance, setMaintenance] = React.useState(null);
  const location = useLocation();

  React.useEffect(() => {
    // Hash anchor scroll
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.getElementById(window.location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 800);
    }

    apiRequest("/api/health")
      .then((d) => setMaintenance(!!d?.maintenance))
      .catch(() => setMaintenance(false));
  }, []);

  if (maintenance === true) {
    return <MaintenancePage />;
  }

  if (location.pathname === "/start" || location.pathname === "/signup") {
    return (
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    );
  }

  return (
    <PropertyProvider>
    <>
      <SystemBanner />
      <PublicLayout>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </PublicLayout>
    </>
  </PropertyProvider>
  );
}

export default App;